import { fetchAuthSession } from 'aws-amplify/auth';
import { lambdaEndpoints } from '../aws-config';
import { SavedFlow, SavedFlowSummary, SerializedNode, SerializedEdge } from '../types/flows';

const BASE_URL = lambdaEndpoints.flows;

class FlowService {
  private async getAuthToken(): Promise<string | null> {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString() || null;
    } catch {
      return null;
    }
  }

  private async request(method: string, path: string = '', body?: any): Promise<any> {
    const token = await this.getAuthToken();
    const url = path ? `${BASE_URL}/${path}` : BASE_URL;

    const res = await fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });

    const data = await res.json();
    if (!res.ok) {
      throw new Error(data.error || `Request failed: ${res.status}`);
    }
    return data;
  }

  async listFlows(): Promise<SavedFlowSummary[]> {
    const data = await this.request('GET');
    return data.flows || [];
  }

  async createFlow(input: {
    name: string;
    nodes: SerializedNode[];
    edges: SerializedEdge[];
    color: string;
    color_hex: string;
    template_id?: string;
  }): Promise<SavedFlow> {
    const data = await this.request('POST', '', input);
    return data.flow;
  }

  async getFlow(flowId: string): Promise<SavedFlow> {
    const data = await this.request('GET', flowId);
    return data.flow;
  }

  async updateFlow(flowId: string, input: {
    name?: string;
    nodes?: SerializedNode[];
    edges?: SerializedEdge[];
    color?: string;
    color_hex?: string;
  }): Promise<void> {
    await this.request('PUT', flowId, input);
  }

  async deleteFlow(flowId: string): Promise<void> {
    await this.request('DELETE', flowId);
  }
}

export const flowService = new FlowService();
